import ArrowSVG from '@public/icons/arrow.svg';
import classNames from 'classnames';
import { AnimatePresence, m as motion } from 'framer-motion';
import { MutableRefObject, forwardRef, useRef, useState } from 'react';
import SwiperConstructor from 'swiper';
import { Thumbs } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import CloseButton from '@/shared/components/configurator/CloseButton';
import Image from '@/shared/components/shared/Image';
import Popup from '@/shared/components/shared/Popup';
import Responsive from '@/shared/components/shared/Responsive';
import { useMounted } from '@/shared/hooks/use-mounted';
import { ImageShape } from '@/shared/lib/types';
import { customToCurrency } from '@/shared/lib/utils/to-currency';

type Props = React.HTMLAttributes<HTMLElement> & {
    slides: {
        title: string;
        img: ImageShape;
        square?: string | number;
        price?: string | number;
    }[];
    title?: string;
};

export const GALLERY_POPUP_NAME = 'constructor-gallery-popup';

const GalleryPopup = forwardRef<HTMLDivElement, Props>(({ slides, title, ...props }, ref) => {
    const swiperRef = useRef<SwiperRef>();
    const { closePopup } = useSetOpenedPopupsState();
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperConstructor | null>(null);
    const currentTitle = slides[activeSlideIndex].title;
    const activeSlidePrice = slides[activeSlideIndex].price as number;
    const currentTableData = [
        slides[activeSlideIndex].square,
        typeof activeSlidePrice === 'number' ? customToCurrency(activeSlidePrice) : activeSlidePrice,
    ].filter(Boolean);

    const isMounted = useMounted();

    return (
        <Popup
            {...props}
            name={GALLERY_POPUP_NAME}
            ref={ref}
            className={classNames('constructor-gallery-popup js-constructor-gallery-popup', props.className)}
            wrapperSlot={
                <div className="constructor-gallery-popup-thumbnails-slider-wrapper">
                    {thumbsSwiper && (
                        <button
                            className="constructor-gallery-popup-thumbnails-slider__nav-btn constructor-gallery-popup-thumbnails-slider__nav-btn--prev"
                            aria-label="Предыдущий слайд"
                            onClick={() => {
                                thumbsSwiper.slidePrev();
                            }}
                        >
                            <ArrowSVG />
                        </button>
                    )}
                    {isMounted && (
                        <Swiper
                            modules={[Thumbs]}
                            onSwiper={setThumbsSwiper}
                            spaceBetween={10}
                            slidesPerView="auto"
                            watchSlidesProgress
                            className="constructor-gallery-popup-thumbnails-slider"
                        >
                            {slides.map((slide, i) => (
                                <SwiperSlide key={i} className="constructor-gallery-popup-thumbnails-slider__slide">
                                    <Responsive>
                                        <Image
                                            src={slide.img.src}
                                            width={slide.img.width}
                                            height={slide.img.height}
                                            alt={slide.img.alt || ''}
                                            sizes="100px"
                                        />
                                    </Responsive>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    )}

                    {thumbsSwiper && (
                        <button
                            className="constructor-gallery-popup-thumbnails-slider__nav-btn constructor-gallery-popup-thumbnails-slider__nav-btn--next"
                            aria-label="Следующий слайд"
                            onClick={() => {
                                thumbsSwiper.slideNext();
                            }}
                        >
                            <ArrowSVG />
                        </button>
                    )}
                </div>
            }
        >
            <div className="constructor-gallery-popup__top wrapper">
                <div>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentTitle}
                            className="h2"
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 5 }}
                            transition={{ duration: 0.3 }}
                        >
                            {currentTitle}
                        </motion.div>
                    </AnimatePresence>
                    <AnimatePresence mode="wait">
                        {currentTableData.length > 0 && (
                            <motion.table
                                key={currentTitle}
                                className="data-table constructor-gallery-popup-table text-l"
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 5 }}
                                transition={{ duration: 0.3 }}
                            >
                                <tbody>
                                    {currentTableData.map((row, i) => (
                                        <tr key={i}>
                                            <td>{i === 0 ? 'Площадь от' : 'Цена от'}</td>
                                            <td
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        i === 0
                                                            ? `(${row} м<sup>2</sup>)`
                                                            : `${customToCurrency(row as number)} ₽/м<sup>2</sup>`,
                                                }}
                                            ></td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td>Стоимость от</td>
                                        <td
                                            dangerouslySetInnerHTML={{
                                                __html: `${customToCurrency(
                                                    (currentTableData[0] as number) * activeSlidePrice,
                                                )} ₽`,
                                            }}
                                        ></td>
                                    </tr>
                                </tbody>
                            </motion.table>
                        )}
                    </AnimatePresence>
                </div>
                <CloseButton
                    text="К схеме"
                    variant="white"
                    onClick={() => closePopup(GALLERY_POPUP_NAME)}
                    className="page-top__close-btn"
                />
                <div className="text-m constructor-gallery-popup__top-right">{title}</div>
            </div>
            {isMounted && (
                <Swiper
                    ref={swiperRef as MutableRefObject<SwiperRef>}
                    modules={[Thumbs]}
                    className="swiper constructor-gallery-popup-slider"
                    speed={600}
                    onSlideChange={(swiper) => {
                        setActiveSlideIndex(swiper.realIndex);
                    }}
                    thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                >
                    {slides.map((slide, i) => (
                        <SwiperSlide key={i} className="constructor-gallery-popup-slider__slide">
                            <Image
                                src={slide.img.src}
                                width={slide.img.width}
                                height={slide.img.height}
                                alt={slide.img.alt || ''}
                                className="constructor-gallery-popup-slider__slide-img"
                                sizes="100vw"
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}
        </Popup>
    );
});

GalleryPopup.displayName = 'GalleryPopup';

export default GalleryPopup;
